import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { ArrowsLeftRightIcon, ChevronRightIcon } from '@root/shared/ui-kit'

import { TransactionListItemAmount } from './transaction-list-item-amount'
import { TransactionListItemTransferRecipient } from './transaction-list-item-transfer-recipient'

import type { TransactionListItemVariant } from './transaction-list-item'
import type { Transaction } from '../../model'

const TX_TYPE_ICON_SIZE = 13

type Props = {
  tx: Transaction.Transaction
  iconSize?: number
  auxIconSize?: number
  variant: TransactionListItemVariant
}

export const TransactionListItemPrices: React.FC<Props> = ({
  tx,
  iconSize = 26,
  variant,
}) => {
  if (tx.type === 'swap') {
    if (!tx.swapInfo) {
      return undefined
    }

    return (
      <Root data-variant={variant}>
        <TransactionListItemAmount
          iconSize={iconSize}
          pairAddress={tx.pairAddress}
          currency={tx.swapInfo.currencyIn}
          secondary={
            <Numeric
              value={tx.swapInfo.inAmountUsd}
              preset="currency-amount-short"
              currency="$"
            />
          }
          primary={
            <Numeric
              value={tx.swapInfo.inAmount}
              preset="currency-amount-short"
              currency={tx.swapInfo.currencyIn.ticker}
            />
          }
        />
        {variant === 'row' ? <SwapIcon width={TX_TYPE_ICON_SIZE} /> : undefined}
        <AmountEnd
          iconSize={iconSize}
          pairAddress={tx.pairAddress}
          currency={tx.swapInfo.currencyOut}
          secondary={
            <Numeric
              value={tx.swapInfo.outAmountUsd}
              preset="currency-amount-short"
              currency="$"
            />
          }
          primary={
            <Numeric
              value={tx.swapInfo.outAmount}
              preset="currency-amount-short"
              currency={tx.swapInfo.currencyOut.ticker}
            />
          }
        />
      </Root>
    )
  }

  if (tx.type === 'transfer') {
    if (!tx.transferInfo) {
      return undefined
    }

    return (
      <Root data-variant={variant}>
        <TransactionListItemAmount
          iconSize={iconSize}
          pairAddress={tx.pairAddress}
          currency={tx.transferInfo.currency}
          secondary={
            <Numeric
              value={tx.transferInfo.valueUsd}
              preset="currency-amount-short"
              currency="$"
            />
          }
          primary={
            <Numeric
              value={tx.transferInfo.value}
              preset="currency-amount-short"
              currency={tx.transferInfo.currency.ticker}
            />
          }
        />
        {variant === 'row' ? <div /> : undefined}
        <TransactionListItemTransferRecipient
          transferInfo={tx.transferInfo}
          variant={variant}
        />
      </Root>
    )
  }
  if (!tx.swapInfo) {
    return undefined
  }

  return (
    <Root data-variant={variant}>
      <TransactionListItemAmount
        iconSize={iconSize}
        pairAddress={tx.pairAddress}
        currency={tx.swapInfo.currencyIn}
        secondary={
          <Numeric
            value={tx.swapInfo.inAmountUsd}
            preset="currency-amount-short"
            currency="$"
          />
        }
        primary={
          <Numeric
            value={tx.swapInfo.inAmount}
            preset="currency-amount-short"
            currency={tx.swapInfo.currencyIn.ticker}
          />
        }
      />
      {variant === 'row' ? (
        <TransferIcon width={TX_TYPE_ICON_SIZE} />
      ) : undefined}
      <AmountEnd
        iconSize={iconSize}
        pairAddress={tx.pairAddress}
        currency={tx.swapInfo.currencyOut}
        secondary={
          <Numeric
            value={tx.swapInfo.outAmountUsd}
            preset="currency-amount-short"
            currency="$"
          />
        }
        primary={
          <Numeric
            value={tx.swapInfo.outAmount}
            preset="currency-amount-short"
            currency={tx.swapInfo.currencyOut.ticker}
          />
        }
      />
    </Root>
  )
}

const Root = styled.div`
  display: grid;

  &[data-variant='row'] {
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    gap: 5px;
  }
  &[data-variant='square'] {
    gap: 4px;
    justify-content: start;
  }
`

const AmountEnd = styled(TransactionListItemAmount)`
  justify-self: end;

  ${Root}[data-variant='square'] & {
    justify-self: start;
  }
`

const SwapIcon = styled(ArrowsLeftRightIcon)`
  color: var(--text-10);
  display: grid;
  place-self: center;
`

const TransferIcon = styled(ChevronRightIcon)`
  color: var(--text-10);
  display: grid;
  place-self: center;
`
